'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface CalendarEvent {
  time: string;
  event: string;
  impact: 'high' | 'medium' | 'low';
  actual: string | null;
  forecast: string;
  previous: string;
  country: string;
  source: 'fred' | 'static';
}

// Static upcoming events — always shown as a baseline
const STATIC_EVENTS: CalendarEvent[] = [
  { time: '08:30', event: 'Initial Jobless Claims', impact: 'high', actual: null, forecast: '225K', previous: '221K', country: 'US', source: 'static' },
  { time: '10:00', event: 'ISM Services PMI', impact: 'medium', actual: null, forecast: '52.8', previous: '52.1', country: 'US', source: 'static' },
  { time: '10:30', event: 'EIA Crude Oil Inventories', impact: 'medium', actual: null, forecast: '-1.2M', previous: '2.3M', country: 'US', source: 'static' },
  { time: '14:00', event: 'Fed Beige Book', impact: 'high', actual: null, forecast: '—', previous: '—', country: 'US', source: 'static' },
  { time: '15:00', event: 'Consumer Credit', impact: 'low', actual: null, forecast: '$12.5B', previous: '$11.4B', country: 'US', source: 'static' },
];

// FRED series to display as economic indicators
const FRED_SERIES: Array<{ id: string; label: string; impact: 'high' | 'medium' | 'low'; time: string }> = [
  { id: 'FEDFUNDS', label: 'Fed Funds Rate', impact: 'high', time: '—' },
  { id: 'CPIAUCSL', label: 'CPI (YoY)', impact: 'high', time: '08:30' },
  { id: 'UNRATE', label: 'Unemployment Rate', impact: 'high', time: '08:30' },
  { id: 'GDP', label: 'GDP Growth Rate', impact: 'high', time: '08:30' },
  { id: 'DGS10', label: '10-Year Treasury Yield', impact: 'medium', time: '—' },
];

const IMPACT_COLORS: Record<string, string> = {
  high: 'bg-red-500',
  medium: 'bg-amber-500',
  low: 'bg-emerald-500',
};

type FilterType = 'all' | 'high' | 'medium' | 'low';

export default function EconomicCalendar() {
  const [filter, setFilter] = useState<FilterType>('all');
  const [events, setEvents] = useState<CalendarEvent[]>(STATIC_EVENTS);
  const [loading, setLoading] = useState(true);
  const [hasFredData, setHasFredData] = useState(false);

  useEffect(() => {
    const fetchFredData = async () => {
      try {
        // Check if FRED is configured
        const statusRes = await fetch('/api/market/ingest');
        const statusJson = await statusRes.json();

        if (!statusJson.hasFRED) {
          setLoading(false);
          return;
        }

        // Fetch each FRED series
        const fredEvents: CalendarEvent[] = [];
        for (const series of FRED_SERIES) {
          try {
            const res = await fetch(`/api/market/ingest?fred=1&series=${series.id}`);
            const json = await res.json();
            if (json.data?.value != null) {
              const val = json.data.value;
              const formatted = series.id === 'GDP'
                ? `${val.toFixed(1)}%`
                : series.id === 'FEDFUNDS' || series.id === 'UNRATE' || series.id === 'DGS10'
                ? `${val.toFixed(2)}%`
                : val.toFixed(1);

              fredEvents.push({
                time: series.time,
                event: series.label,
                impact: series.impact,
                actual: formatted,
                forecast: '—',
                previous: '—',
                country: 'US',
                source: 'fred',
              });
            }
          } catch { /* skip this series */ }
        }

        if (fredEvents.length > 0) {
          setHasFredData(true);
          // Merge FRED data at the top, then static events
          setEvents([...fredEvents, ...STATIC_EVENTS]);
        }
      } catch { /* keep static events */ } finally {
        setLoading(false);
      }
    };

    fetchFredData();
  }, []);

  const filtered = events.filter(e => filter === 'all' || e.impact === filter);

  return (
    <div className="bg-[#1E293B] border border-[#334155] rounded-lg overflow-hidden flex flex-col">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#334155] shrink-0">
        <div className="flex items-center gap-2">
          <Icon name="CalendarDaysIcon" size={16} variant="solid" className="text-emerald-500" />
          <span className="text-sm font-semibold text-slate-200 font-mono">Economic Calendar</span>
          {loading && <div className="w-3 h-3 border border-emerald-500 border-t-transparent rounded-full animate-spin" />}
          {hasFredData && !loading && (
            <span className="flex items-center gap-1 text-xs text-emerald-400 font-mono">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              FRED
            </span>
          )}
        </div>
        <select
          value={filter}
          onChange={e => setFilter(e.target.value as FilterType)}
          className="text-xs bg-[#334155] text-slate-300 border border-[#475569] rounded px-2 py-1 outline-none"
        >
          <option value="all">All</option>
          <option value="high">High Impact</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
      </div>

      <div className="flex-1 overflow-y-auto divide-y divide-[#334155]/50">
        {filtered.map((event, i) => (
          <div key={i} className="flex items-start gap-3 px-4 py-3 hover:bg-[#334155]/20 transition-all">
            <div className="shrink-0 text-right w-10">
              <p className="text-xs font-mono text-slate-400">{event.time}</p>
              <p className="text-xs text-slate-600">{event.country}</p>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${IMPACT_COLORS[event.impact]}`} />
                <p className="text-xs font-medium text-slate-200 truncate">{event.event}</p>
                {event.source === 'fred' && (
                  <span className="text-xs text-emerald-600 font-mono shrink-0">FRED</span>
                )}
              </div>
              <div className="flex items-center gap-3 text-xs font-mono flex-wrap">
                {event.actual !== null ? (
                  <span className="font-bold text-emerald-400">
                    A: {event.actual}
                  </span>
                ) : (
                  <span className="text-slate-600 italic">Pending</span>
                )}
                {event.forecast !== '—' && <span className="text-slate-500">F: {event.forecast}</span>}
                {event.previous !== '—' && <span className="text-slate-600">P: {event.previous}</span>}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="px-4 py-2 border-t border-[#334155] flex items-center justify-between shrink-0">
        <div className="flex items-center gap-4">
          {(['high', 'medium', 'low'] as const).map(impact => (
            <div key={impact} className="flex items-center gap-1">
              <div className={`w-1.5 h-1.5 rounded-full ${IMPACT_COLORS[impact]}`} />
              <span className="text-xs text-slate-500 capitalize">{impact}</span>
            </div>
          ))}
        </div>
        {!hasFredData && !loading && (
          <a href="/user-settings" className="text-xs text-slate-600 hover:text-slate-400 font-mono transition-all">
            + FRED key
          </a>
        )}
      </div>
    </div>
  );
}